import { toRenderSlot, type AppState, type EditorContext, type EditorGraphMode, type EditorGraphSlot } from '../../state/appState.js';
import { cloneEditorContext } from '../documentClone.js';
import { loadLibraryGraphIntoEditorFor } from '../editorSession.js';
import type { ActionBase, ActionExecution } from '../commandExecutor.js';

export interface EditorSessionState {
  activeComponentId: string | null;
  editorGraphSlot: EditorGraphSlot;
  editorGraphMode: EditorGraphMode;
  editorContext: EditorContext;
}

export interface RestoreEditorSessionAction extends ActionBase<'editor:restoreSession'> {
  session: EditorSessionState;
}

export function captureEditorSessionFor(appState: AppState): EditorSessionState {
  return {
    activeComponentId: appState.activeComponentId,
    editorGraphSlot: appState.editorGraphSlot,
    editorGraphMode: appState.editorGraphMode,
    editorContext: cloneEditorContext(appState.editorContext),
  };
}

export interface EditorTargetRecoveryHooks {
  loadComponentSlot?: (componentId: string | null, slot: 'geometry' | 'material', layerId?: string) => boolean;
}

export function resetEditorAfterComponentRemovalFor(
  appState: AppState,
  hooks: EditorTargetRecoveryHooks = {},
): void {
  const firstId = Object.keys(appState.componentLibrary)[0] ?? null;
  appState.editorGraphDirty = false;
  appState.selectedNode = null;

  if (!firstId) {
    if (!hooks.loadComponentSlot?.(null, toRenderSlot(appState.editorGraphSlot))) {
      appState.activeComponentId = null;
      appState.editorContext = {
        mode: 'layer',
        componentId: undefined,
        layerId: undefined,
        layerSlot: toRenderSlot(appState.editorGraphSlot),
      };
      appState.nodes = [];
      appState.edges = [];
    }
    return;
  }

  if (!hooks.loadComponentSlot?.(firstId, toRenderSlot(appState.editorGraphSlot))) {
    appState.nodes = [];
    appState.edges = [];
  }
}

export function recoverEditorTargetFor(
  appState: AppState,
  hooks: EditorTargetRecoveryHooks = {},
): void {
  const ctx = appState.editorContext;
  if (ctx.mode === 'layer') {
    const componentId = ctx.componentId ?? null;
    const ownerExists = componentId === null || Boolean(appState.componentLibrary[componentId]);
    if (ctx.layerId && appState.layerLibrary[ctx.layerId] && ownerExists) return;
    if (hooks.loadComponentSlot?.(componentId, toRenderSlot(appState.editorGraphSlot), ctx.layerId)) return;
    appState.nodes = [];
    appState.edges = [];
    appState.editorGraphDirty = false;
    appState.selectedNode = null;
    appState.editorContext = {
      mode: 'layer',
      componentId: ctx.componentId,
      layerId: undefined,
      layerSlot: toRenderSlot(appState.editorGraphSlot),
    };
  }
}

export interface RestoreEditorSessionHooks {
  loadComponentSlot?: (componentId: string | null, slot: 'geometry' | 'material', layerId?: string) => boolean;
  resetEditorAfterComponentRemoval?: () => void;
}

export function restoreEditorSessionFor(
  appState: AppState,
  session: EditorSessionState,
  hooks: RestoreEditorSessionHooks = {},
): void {
  appState.editorGraphDirty = false;
  appState.selectedNode = null;

  const nextContext = cloneEditorContext(session.editorContext);
  appState.editorContext = nextContext;
  appState.editorGraphSlot = session.editorGraphSlot;
  appState.editorGraphMode = session.editorGraphMode;
  appState.activeComponentId = session.activeComponentId;

  if (
    nextContext.mode === 'layer'
    && (nextContext.componentId === undefined || appState.componentLibrary[nextContext.componentId])
  ) {
    hooks.loadComponentSlot?.(
      nextContext.componentId ?? null,
      toRenderSlot(nextContext.layerSlot ?? session.editorGraphSlot),
      nextContext.layerId,
    );
    return;
  }

  if (nextContext.mode === 'geometry-library' && nextContext.libraryItemId) {
    const item = appState.geometryLibrary.user[nextContext.libraryItemId]
      ?? appState.geometryLibrary.builtIn[nextContext.libraryItemId];
    if (item) {
      loadLibraryGraphIntoEditorFor(appState, 'geometry', item);
      return;
    }
  }

  if (nextContext.mode === 'material-library' && nextContext.libraryItemId) {
    const item = appState.materialLibrary.user[nextContext.libraryItemId]
      ?? appState.materialLibrary.builtIn[nextContext.libraryItemId];
    if (item) {
      loadLibraryGraphIntoEditorFor(appState, 'material', item);
      return;
    }
  }

  if (hooks.resetEditorAfterComponentRemoval) {
    hooks.resetEditorAfterComponentRemoval();
  } else {
    resetEditorAfterComponentRemovalFor(appState, hooks);
  }
}

export function execRestoreEditorSessionFor(
  appState: AppState,
  cmd: RestoreEditorSessionAction,
  hooks: RestoreEditorSessionHooks = {},
): ActionExecution<void> {
  restoreEditorSessionFor(appState, cmd.session, hooks);
  return { result: undefined, undoActions: [], changed: true };
}
